import type { IntakeResult, IntakeCheck } from "../api/types.js";
import { StatusBadge } from "./status-badge.js";

interface IntakeSummaryProps {
  result: IntakeResult;
}

interface CheckRow {
  key: keyof IntakeCheck;
  label: string;
  passValue?: unknown;
}

const CHECK_ROWS: CheckRow[] = [
  { key: "dnsResolved", label: "DNS resolved", passValue: true },
  { key: "httpsAvailable", label: "HTTPS available", passValue: true },
  { key: "robotsTxtFound", label: "robots.txt found", passValue: true },
  { key: "sitemapFound", label: "Sitemap found", passValue: true },
  { key: "loginFormDetected", label: "Login form detected", passValue: false },
  { key: "pageLoadSlow", label: "Slow page load", passValue: false },
];

function checkItemClass(
  checkKey: keyof IntakeCheck,
  checks: IntakeCheck,
): string {
  const value = checks[checkKey];
  if (
    checkKey === "cmsDetected" ||
    checkKey === "wafDetected" ||
    checkKey === "pageLoadMs"
  ) {
    return "intake-check-item--neutral";
  }
  const row = CHECK_ROWS.find((r) => r.key === checkKey);
  if (!row) return "intake-check-item--neutral";
  return value === row.passValue
    ? "intake-check-item--pass"
    : "intake-check-item--fail";
}

export function IntakeSummary({ result }: IntakeSummaryProps) {
  const { checks, hardFail, hardFailReason, warnings, advisories } = result;

  return (
    <div className="intake-summary" data-testid="intake-summary">
      {/* Overall status banner */}
      {hardFail ? (
        <div className="intake-summary__status-row intake-summary__status-row--fail">
          <StatusBadge variant="fail" />
          <span>
            {hardFailReason ??
              "This website cannot be scanned until the issue is resolved."}
          </span>
        </div>
      ) : (
        <div className="intake-summary__status-row intake-summary__status-row--pass">
          <StatusBadge variant="pass" label="Ready to continue" />
          <span>
            {warnings.length > 0 || advisories.length > 0
              ? "Scan can proceed. Some items may affect results."
              : "All checks passed."}
          </span>
        </div>
      )}

      {/* Individual check results */}
      <div>
        <p className="section-title">Check results</p>
        <div className="intake-checks-grid">
          {CHECK_ROWS.map(({ key, label }) => {
            const cls = checkItemClass(key, checks);
            const value = checks[key];
            const icon =
              cls === "intake-check-item--pass"
                ? "✓"
                : cls === "intake-check-item--fail"
                  ? "✗"
                  : "–";
            return (
              <div key={key} className={`intake-check-item ${cls}`}>
                <span aria-hidden="true">{icon}</span>
                <span>{label}</span>
                {typeof value === "number" ? (
                  <span className="text-muted text-sm">({value} ms)</span>
                ) : null}
              </div>
            );
          })}
          {checks.cmsDetected && (
            <div className="intake-check-item intake-check-item--neutral">
              <span aria-hidden="true">–</span>
              <span>CMS: {checks.cmsDetected}</span>
            </div>
          )}
          {checks.wafDetected && (
            <div className="intake-check-item intake-check-item--neutral">
              <span aria-hidden="true">–</span>
              <span>WAF: {checks.wafDetected}</span>
            </div>
          )}
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div>
          <p className="section-title">
            Warnings{" "}
            <StatusBadge variant="warning" label={`${warnings.length}`} />
          </p>
          <p className="text-sm text-muted mb-2">
            Warnings will not block the scan but may affect accuracy.
          </p>
          <ul className="issue-list" aria-label="Warnings">
            {warnings.map((w) => (
              <li
                key={w.code}
                className="issue-list__item issue-list__item--warning"
              >
                <strong>{w.code}</strong> — {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Advisories */}
      {advisories.length > 0 && (
        <div>
          <p className="section-title">
            Advisories{" "}
            <StatusBadge variant="advisory" label={`${advisories.length}`} />
          </p>
          <p className="text-sm text-muted mb-2">
            Advisories are informational. You can still proceed.
          </p>
          <ul className="issue-list" aria-label="Advisories">
            {advisories.map((a) => (
              <li
                key={a.code}
                className="issue-list__item issue-list__item--advisory"
              >
                <strong>{a.code}</strong> — {a.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
